import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  COOKIE_CONSENT_EVENT,
  COOKIE_SETTINGS_EVENT,
  hasCookieDecision,
  readCookieConsent,
  saveCookieConsent,
} from "@/lib/cookieConsent";

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState(() => readCookieConsent());

  useEffect(() => {
    const sync = () => {
      const next = readCookieConsent();
      setDraft(next);
      setVisible(!hasCookieDecision());
    };
    sync();

    const onOpenSettings = () => {
      setDraft(readCookieConsent());
      setSettingsOpen(true);
      setVisible(true);
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, sync);
    window.addEventListener(COOKIE_SETTINGS_EVENT, onOpenSettings);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, sync);
      window.removeEventListener(COOKIE_SETTINGS_EVENT, onOpenSettings);
    };
  }, []);

  const summary = useMemo(() => {
    if (draft.analytics && draft.speedInsights) return "Ai activat analytics si performance insights.";
    if (!draft.analytics && !draft.speedInsights) return "Ai ales doar cookie-urile necesare.";
    if (draft.analytics) return "Ai activat analytics, dar ai lasat performance insights oprite.";
    return "Ai activat performance insights, dar ai lasat analytics oprite.";
  }, [draft.analytics, draft.speedInsights]);

  const acceptAll = () => {
    saveCookieConsent({
      necessary: true,
      analytics: true,
      speedInsights: true,
      source: "accept-all",
    });
    setSettingsOpen(false);
    setVisible(false);
  };

  const acceptNecessaryOnly = () => {
    saveCookieConsent({
      necessary: true,
      analytics: false,
      speedInsights: false,
      source: "necessary-only",
    });
    setSettingsOpen(false);
    setVisible(false);
  };

  const saveCustom = () => {
    saveCookieConsent({
      necessary: true,
      analytics: Boolean(draft.analytics),
      speedInsights: Boolean(draft.speedInsights),
      source: "custom-settings",
    });
    setSettingsOpen(false);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {(visible || settingsOpen) && (
        <>
          {settingsOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[129] bg-black/60 backdrop-blur-sm"
              onClick={() => setSettingsOpen(false)}
            />
          )}

          {visible && (
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="fixed left-3 right-3 sm:left-6 sm:right-6 bottom-3 sm:bottom-6 z-[120]"
              data-testid="cookie-banner"
            >
              <div className="glass-strong rounded-2xl sm:rounded-3xl p-4 sm:p-5 border border-[var(--border-strong)] shadow-[var(--shadow-card)]">
                <p className="text-sm text-[var(--text-dim)] leading-relaxed" data-testid="cookie-banner-text">
                  Acest website foloseste cookie-uri necesare pentru functionarea corecta. Optional, poti permite
                  analytics si performance insights anonimizate. Pentru mai multe detalii, consulta{" "}
                  <Link to="/privacy-policy" className="text-[var(--accent)] hover:opacity-90">
                    Politica de Confidentialitate
                  </Link>{" "}
                  si{" "}
                  <Link to="/cookies-policy" className="text-[var(--accent)] hover:opacity-90">
                    Politica de Cookies
                  </Link>.
                </p>

                <div className="mt-4 flex flex-wrap gap-2 sm:gap-3 items-center">
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center justify-center"
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </button>
                  <button
                    type="button"
                    className="btn-ghost inline-flex items-center justify-center"
                    onClick={acceptNecessaryOnly}
                    data-testid="cookie-necessary-only"
                  >
                    Necessary only
                  </button>
                  <button
                    type="button"
                    className="btn-ghost inline-flex items-center justify-center"
                    onClick={() => setSettingsOpen(true)}
                    data-testid="cookie-open-settings"
                  >
                    Settings
                  </button>
                </div>
              </div>
            </motion.aside>
          )}

          {settingsOpen && (
            <motion.div
              initial={{ opacity: 0, y: 14, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 14, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="fixed left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 bottom-3 sm:bottom-6 z-[130] w-auto sm:w-full sm:max-w-xl"
              data-testid="cookie-settings-modal"
            >
              <div className="glass-strong rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-[var(--border-strong)] shadow-[var(--shadow-card)]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-semibold">Cookie Settings</h3>
                    <p className="mt-2 text-sm text-[var(--text-dim)] leading-relaxed">
                      Poti alege daca permiti statistici anonimizate despre trafic si metrici tehnice de performanta.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost !px-3 !py-2"
                    onClick={() => setSettingsOpen(false)}
                    data-testid="cookie-settings-close"
                  >
                    Close
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  <div className="glass rounded-2xl p-4 border border-[var(--border)]">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">Necessary cookies</div>
                        <p className="mt-1 text-sm text-[var(--text-dim)]">
                          Necesare pentru functionarea de baza a website-ului. Acestea nu pot fi dezactivate.
                        </p>
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-[var(--text)]">
                        <input type="checkbox" checked disabled />
                        On
                      </label>
                    </div>
                  </div>

                  <div className="glass rounded-2xl p-4 border border-[var(--border)]">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">Web Analytics</div>
                        <p className="mt-1 text-sm text-[var(--text-dim)]">
                          Statistici anonimizate despre trafic, pagini vizitate, surse si tipuri de dispozitive.
                        </p>
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-[var(--text)]">
                        <input
                          type="checkbox"
                          checked={!!draft.analytics}
                          onChange={(e) => setDraft((prev) => ({ ...prev, analytics: e.target.checked }))}
                          data-testid="cookie-toggle-analytics"
                        />
                        {draft.analytics ? "On" : "Off"}
                      </label>
                    </div>
                  </div>

                  <div className="glass rounded-2xl p-4 border border-[var(--border)]">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">Speed Insights</div>
                        <p className="mt-1 text-sm text-[var(--text-dim)]">
                          Metrici tehnice anonimizate despre viteza de incarcare si experienta reala a paginilor.
                        </p>
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-[var(--text)]">
                        <input
                          type="checkbox"
                          checked={!!draft.speedInsights}
                          onChange={(e) => setDraft((prev) => ({ ...prev, speedInsights: e.target.checked }))}
                          data-testid="cookie-toggle-speed"
                        />
                        {draft.speedInsights ? "On" : "Off"}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-[var(--accent)]/25 bg-[var(--accent)]/10 p-3 text-sm text-[var(--text-dim)]">
                  {summary}
                </div>

                <div className="mt-5 flex flex-wrap gap-2 sm:gap-3">
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center justify-center"
                    onClick={saveCustom}
                    data-testid="cookie-save-settings"
                  >
                    Save settings
                  </button>
                  <button
                    type="button"
                    className="btn-ghost inline-flex items-center justify-center"
                    onClick={acceptNecessaryOnly}
                    data-testid="cookie-reject-optional"
                  >
                    Reject optional
                  </button>
                  <button
                    type="button"
                    className="btn-ghost inline-flex items-center justify-center"
                    onClick={acceptAll}
                    data-testid="cookie-accept-all-settings"
                  >
                    Accept all
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
